import React, { useState, useEffect } from 'react';

const STORAGE_KEY = 'sugarhathi_install_dismissed';
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

function isMobileDevice() {
  const ua = navigator.userAgent || '';
  return /android|iphone|ipad|ipod|mobile/i?.test(ua);
}

function isIOS() {
  const ua = navigator.userAgent || '';
  return /iphone|ipad|ipod/i?.test(ua);
}

function isStandalone() {
  return (window.matchMedia('(display-mode: standalone)')?.matches || window.navigator?.standalone === true);
}

function isDismissed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { timestamp } = JSON.parse(raw);
    return Date.now() - timestamp < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Only show on mobile
    if (!isMobileDevice()) return;
    // Don't show if already installed (standalone mode)
    if (isStandalone()) return;
    // Don't show if dismissed within 7 days
    if (isDismissed()) return;

    if (isIOS()) {
      // iOS Safari: show banner after short delay (no beforeinstallprompt on iOS)
      const timer = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(timer);
    }

    // Android / Chrome: wait for beforeinstallprompt
    const handler = (e) => {
      e?.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    window.addEventListener('appinstalled', () => {
      setVisible(false);
      setDeferredPrompt(null);
    });

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS()) {
      setShowIOSGuide(true);
      return;
    }
    if (!deferredPrompt) return;
    deferredPrompt?.prompt();
    const { outcome } = await deferredPrompt?.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
    if (outcome === 'accepted') {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamp: Date.now() })); } catch {}
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    setShowIOSGuide(false);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ timestamp: Date.now() }));
    } catch {}
  };

  if (!visible) return null;

  return (
    <>
      {/* Install Banner — bottom of screen, dark theme, non-intrusive */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 px-3 pb-3 pt-0"
        role="banner"
        aria-label="Install SugarHathi App"
        style={{ pointerEvents: 'none' }}
      >
        <div
          className="flex items-center gap-3 rounded-2xl px-4 py-3 shadow-2xl"
          style={{
            background: 'linear-gradient(135deg, #1a2e44 0%, #0f1e2e 100%)',
            border: '1px solid rgba(255,255,255,0.08)',
            pointerEvents: 'auto',
            backdropFilter: 'blur(12px)',
          }}
        >
          {/* Logo */}
          <div
            className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden"
            style={{ background: 'rgba(234,88,12,0.15)', border: '1px solid rgba(234,88,12,0.3)' }}
          >
            <img
              src="/assets/images/ChatGPT_Image_Dec_1_2025_08_15_51_PM-1764613791088.png"
              alt="SugarHathi"
              className="w-8 h-8 object-contain rounded-lg"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentElement.innerHTML = '<span style="font-size:18px">🍬</span>';
              }}
            />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white leading-tight truncate">
              Install SugarHathi for faster access
            </p>
            {isIOS() && (
              <p className="text-xs mt-0.5 leading-tight" style={{ color: 'rgba(255,255,255,0.5)' }}>
                Tap Share → Add to Home Screen
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={handleInstall}
              className="px-3 py-1.5 text-white text-xs font-bold rounded-lg transition-all active:scale-95"
              style={{ background: 'linear-gradient(135deg, #ea580c, #c2410c)', boxShadow: '0 2px 8px rgba(234,88,12,0.4)' }}
            >
              Install
            </button>
            <button
              onClick={handleDismiss}
              className="px-3 py-1.5 text-xs font-medium rounded-lg transition-all active:scale-95"
              style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.1)' }}
            >
              Not now
            </button>
          </div>
        </div>
      </div>
      {/* iOS Step-by-step Guide Modal */}
      {showIOSGuide && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={(e) => { if (e?.target === e?.currentTarget) handleDismiss(); }}
        >
          <div
            className="w-full max-w-sm rounded-2xl p-5 shadow-2xl"
            style={{ background: '#1a2e44', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold text-white">Add to Home Screen</h3>
              <button
                onClick={handleDismiss}
                className="w-7 h-7 rounded-full flex items-center justify-center transition-colors"
                style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}
                aria-label="Close"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              {[
                { step: 1, text: 'Tap the Share button (□↑) at the bottom of Safari' },
                { step: 2, text: 'Scroll down and tap "Add to Home Screen"' },
                { step: 3, text: 'Tap "Add" in the top-right corner' },
              ]?.map(({ step, text }) => (
                <div key={step} className="flex items-start gap-3">
                  <div
                    className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ background: 'linear-gradient(135deg, #ea580c, #c2410c)' }}
                  >
                    {step}
                  </div>
                  <p className="text-sm pt-0.5" style={{ color: 'rgba(255,255,255,0.8)' }}>{text}</p>
                </div>
              ))}
            </div>

            <button
              onClick={handleDismiss}
              className="mt-5 w-full py-2.5 text-white text-sm font-bold rounded-xl transition-all active:scale-95"
              style={{ background: 'linear-gradient(135deg, #ea580c, #c2410c)' }}
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPrompt;
